import React, { useState, useEffect } from 'react';
import { Ticket } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { createPayment } from '../services/stripeService';
import { toast } from '../utils/toast';
import { supabase } from '../lib/supabase';

interface PurchaseTicketButtonProps {
  calendarId: string;
  eventId: string;
  price: number;
  hasPurchased: boolean;
}

export default function PurchaseTicketButton({
  calendarId,
  eventId,
  price,
  hasPurchased
}: PurchaseTicketButtonProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [ticketLimit, setTicketLimit] = useState<number | null>(null);
  const [soldTickets, setSoldTickets] = useState(0);

  useEffect(() => {
    const fetchTicketInfo = async () => {
      try {
        // Get ticket limit
        const { data: calendar } = await supabase
          .from('calendars')
          .select('event_ticket_limit')
          .eq('id', calendarId)
          .single();

        if (calendar?.event_ticket_limit) {
          setTicketLimit(calendar.event_ticket_limit);
        }

        // Get sold tickets count
        const { count } = await supabase
          .from('event_tickets')
          .select('id', { count: 'exact', head: true })
          .eq('calendar_id', calendarId)
          .eq('event_id', eventId)
          .eq('status', 'paid');

        setSoldTickets(count || 0);
      } catch (err) {
        console.error('Error fetching ticket info:', err);
      }
    };

    fetchTicketInfo();
  }, [calendarId, eventId]);

  const handlePurchase = async () => {
    if (!user) {
      toast.error('Please log in to purchase a ticket');
      return;
    }

    if (ticketLimit && soldTickets >= ticketLimit) {
      toast.error('Sorry, this event is sold out!');
      return;
    }

    try {
      setLoading(true);
      
      // Validate required parameters
      if (!calendarId || !eventId || !price) {
        throw new Error('Missing required purchase parameters');
      }

      const params = {
        calendarId,
        userId: user.id,
        eventId,
        returnUrl: window.location.href
      };

      const response = await createPayment(params);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to process payment');
      }

      const data = await response.json();
      if (!data.url) {
        throw new Error('Invalid response from payment service');
      }

      window.location.assign(data.url);
    } catch (err) {
      console.error('Error purchasing ticket:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to process payment');
    } finally {
      setLoading(false);
    }
  };

  const remainingTickets = ticketLimit ? ticketLimit - soldTickets : null;
  const isSoldOut = ticketLimit && remainingTickets <= 0;

  return (
    <div>
      <button
        onClick={handlePurchase}
        disabled={loading || hasPurchased || isSoldOut}
        className={`w-full flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium ${
          hasPurchased
            ? 'text-gray-700 bg-gray-100 cursor-not-allowed'
            : isSoldOut
            ? 'text-red-700 bg-red-100 cursor-not-allowed'
            : 'text-white bg-purple-600 hover:bg-purple-700'
        } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50`}
      >
        <Ticket className="h-4 w-4 mr-2" />
        {loading ? 'Processing...' : 
         hasPurchased ? 'Purchased' :
         isSoldOut ? 'Sold Out!' :
         `Purchase Ticket - $${price / 100}`}
      </button>
      
      {ticketLimit && !hasPurchased && !isSoldOut && (
        <p className="mt-2 text-sm text-gray-600 text-center">
          {remainingTickets === 1 ? (
            <span className="text-red-600 font-medium">Only 1 ticket remaining!</span>
          ) : (
            `${remainingTickets} tickets remaining`
          )}
        </p>
      )}
    </div>
  );
}